/**
 * Helper qui transforme une `configuration` brute en lignes de description
 * lisibles par un humain. Centralisé pour être réutilisé dans le panier,
 * le checkout et la confirmation de paiement.
 *
 * Toutes les libellés sont en français — l'i18n complet sera fait plus tard
 * si nécessaire pour le marché DE/EN/IT.
 */

const CADUCEE_FINISH_LABELS = {
  "gold-brillant": "Gold brillant",
  "gold-brosse": "Gold brossé",
  "cuivre-poli": "Cuivre poli",
  "cuivre-brosse": "Cuivre brossé",
  "inox-poli": "Inox poli miroir",
  "inox-brosse": "Inox brossé",
};

const CADUCEE_LED_LABELS = {
  warm3000: "Blanc chaud 3000K",
  cool6500: "Blanc froid 6500K",
  blue: "Bleu",
  green: "Vert",
  red: "Rouge",
};

const CROSS_FACE_LABELS = { simple: "Simple face", double: "Double face" };

const CROSS_FAMILY_LABELS = {
  // Familles LED (schéma legacy)
  classic: "Classique",
  dotmatrix: "Point à point",
  neon: "Néon LED",
  // Familles de forme (schéma composition — item.family)
  croix: "Croix latine",
  "croix-fleche": "Croix flèche",
  ronde: "Ronde / Médaillon",
  multi: "Multi-croix",
  "ronde-multi": "Ronde multi-croix",
  angle: "Croix d'angle",
};

function describeCaducee(cfg) {
  const lines = [];
  if (cfg.designId) lines.push(`Modèle n°${cfg.designId}`);
  if (cfg.sizeCm) lines.push(`Hauteur : ${cfg.sizeCm} cm`);
  if (cfg.finishType === "ral" && cfg.ralId) {
    lines.push(`Finition : RAL ${String(cfg.ralId).replace("RAL", "")}${cfg.ralHex ? "" : ""}`);
  } else if (cfg.finishId) {
    lines.push(`Finition : ${CADUCEE_FINISH_LABELS[cfg.finishId] || cfg.finishId}`);
  }
  if (cfg.ledId) lines.push(`LED : ${CADUCEE_LED_LABELS[cfg.ledId] || cfg.ledId}`);
  lines.push(`Pose : ${cfg.placement === "exterieur" ? "Extérieur (IP65)" : "Intérieur"}`);
  return lines;
}

const CROSS_COLOR_LABELS = {
  vert: "Vert", "bleu-fonce": "Bleu foncé", "bleu-clair": "Bleu clair",
  rouge: "Rouge", orange: "Orange", jaune: "Jaune",
  blanc: "Blanc", magenta: "Magenta",
};

function resolveDesignLabel(designId, lang = "fr") {
  if (!designId || typeof window === "undefined") return designId;
  const registry = window.__SIGNAPHARMA_CROSS_DESIGNS__;
  if (!Array.isArray(registry)) return designId;
  const design = registry.find((d) => d?.id === designId);
  if (!design?.label) return designId;
  return design.label[lang] || design.label.fr || designId;
}

function describeCrossItem(item, lang = "fr") {
  const parts = [];
  if (item.family) parts.push(CROSS_FAMILY_LABELS[item.family] || item.family);
  if (item.designId && !String(item.designId).startsWith("_empty_")) {
    parts.push(resolveDesignLabel(item.designId, lang));
  }
  if (item.sizeCm) parts.push(`${item.sizeCm} cm`);
  if (item.colorId) parts.push(`LED ${CROSS_COLOR_LABELS[item.colorId] || item.colorId}`);
  if (item.face) parts.push(CROSS_FACE_LABELS[item.face] || item.face);
  return parts.join(" · ");
}

function describeCross(cfg, lang = "fr") {
  const lines = [];
  // Format moderne : composition multi-items
  if (Array.isArray(cfg.items) && cfg.items.length > 0) {
    const realItems = cfg.items.filter(
      (it) => it && !String(it.designId || "").startsWith("_empty_")
    );
    realItems.forEach((item, i) => {
      const desc = describeCrossItem(item, lang);
      if (desc) {
        lines.push(realItems.length > 1 ? `Élément ${i + 1} : ${desc}` : desc);
      }
    });
    return lines;
  }
  // Format legacy : champs uniques
  if (cfg.family) lines.push(`Type : Croix ${CROSS_FAMILY_LABELS[cfg.family] || cfg.family}`);
  const legacyDesign = cfg.designId || cfg.shapeId;
  if (legacyDesign) lines.push(`Modèle : ${resolveDesignLabel(legacyDesign, lang)}`);
  if (cfg.sizeCm || cfg.sizePx) lines.push(`Taille : ${cfg.sizeCm || cfg.sizePx} cm`);
  if (cfg.face) lines.push(CROSS_FACE_LABELS[cfg.face] || cfg.face);
  if (cfg.colorId) lines.push(`Couleur LED : ${CROSS_COLOR_LABELS[cfg.colorId] || cfg.colorId}`);
  if (cfg.frame) lines.push(`Cadre : ${cfg.frame}`);
  return lines;
}

function describeLetters(cfg) {
  const lines = [];
  if (Array.isArray(cfg.lines) && cfg.lines.length > 0) {
    cfg.lines.forEach((l, i) => {
      const text = (l.text || "").trim();
      const size = l.customHeightCm || (l.sizeId || "").replace("cm", "");
      if (text) lines.push(`Ligne ${i + 1} : « ${text} »${size ? ` — ${size} cm` : ""}`);
    });
  }
  if (cfg.material) lines.push(`Matériau : ${cfg.material}`);
  if (cfg.ralId) lines.push(`RAL : ${String(cfg.ralId).replace("RAL", "")}`);
  if (cfg.ledColor) lines.push(`LED : ${cfg.ledColor}`);
  if (cfg.chassisExtra) lines.push("+ Châssis : oui");
  return lines;
}

/**
 * Renvoie un tableau de strings décrivant la configuration d'un produit.
 * Si rien n'est reconnu, fallback générique sur les clés brutes.
 */
export function describeConfiguration(slug, cfg, lang = "fr") {
  if (!cfg || typeof cfg !== "object") return [];
  if (slug === "caducee") return describeCaducee(cfg);
  if (slug && slug.startsWith("croix-")) return describeCross(cfg, lang);
  if (slug && (slug.startsWith("lettre-") || slug.startsWith("enseigne-"))) {
    return describeLetters(cfg);
  }
  // Fallback générique : on prend les valeurs scalaires les plus pertinentes
  const EXCLUDED = new Set(["priceCHF", "unit_price_chf", "previewImage"]);
  return Object.entries(cfg)
    .filter(([k, v]) => typeof v !== "object" && !EXCLUDED.has(k))
    .slice(0, 6)
    .map(([k, v]) => `${k} : ${v}`);
}

/** Résumé compact en une seule ligne (utilisé pour les metadata Stripe). */
export function summarizeConfiguration(slug, cfg, maxLen = 480) {
  const desc = describeConfiguration(slug, cfg).join(" · ");
  return desc.length > maxLen ? desc.slice(0, maxLen - 1) + "…" : desc;
}
